import { useState, useRef, useEffect } from "react"
import { Circle, Loader2 } from 'lucide-react'

import { getUID, API } from "../../../Helper"
import styled from "styled-components"

const Container = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  background: #292d2e;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    border-radius: 0;
    height: 100vh;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  background: rgba(0, 0, 0, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const Title = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: 0.5px;
`

const MarkAllButton = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #25ee89;
  padding: 8px 16px;
  border-radius: 24px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const TabContainer = styled.div`
  display: flex;
  padding: 8px;
  background: rgba(0, 0, 0, 0.05);
  gap: 8px;
`

const TabButton = styled.div`
  flex: 1;
  padding: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  text-align: center;
  background: ${(props) => (props.active ? "#25ee89" : "transparent")};
  border-radius: 12px;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: ${(props) => (props.active ? "#25ee89" : "rgba(255, 255, 255, 0.1)")};
  }
`

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`

const NotificationItemWrapper = styled.div`
  padding: 16px;
  margin-bottom: 8px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${(props) =>
    props.unread
      ? `rgba(255, 255, 255, 0.18)`
      : `rgba(255, 255, 255, 0.18)`};

  &:hover {
    background: rgba(255, 255, 255, 0.18);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 6px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const NotificationTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
`

const UnreadDot = styled(Circle)`
  width: 6px;
  height: 6px;
  fill: #FF6B8B;
`

const Timestamp = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
`

const NotificationContent = styled.div`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
`

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: rgba(255, 255, 255, 0.7);
  font-size: 15px;
  font-weight: 500;
`

const LoadingSpinner = styled(Loader2)`
  animation: spin 1s linear infinite;
  margin: 32px auto;
  display: block;
  color: rgba(255, 255, 255, 0.9);
  
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const MobileNotification = () => {
  const [activeTab, setActiveTab] = useState("unread");
  const [notifications, setNotifications] = useState({ unread: [], read: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = async () => {
    try {
      const response = await fetch(`${API}/firebase/unread-count/${getUID()}`);
      const data = await response.json();
      if (data.success) {
        setUnreadCount(data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/firebase/history/${getUID()}`);
      const data = await response.json();
      if (data.success) {
        setNotifications({
          unread: data.notifications.unread || [],
          read: data.notifications.read || []
        });
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const response = await fetch(`${API}/firebase/mark-as-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: getUID(),
          notificationIds: [id]
        })
      });

      if (response.ok) {
        setNotifications(prev => {
          const updatedUnread = prev.unread.filter(n => n.id !== id);
          const movedNotification = prev.unread.find(n => n.id === id);
          return {
            unread: updatedUnread,
            read: movedNotification ? [...prev.read, { ...movedNotification, is_read: true }] : prev.read
          };
        });
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.unread.map(notification => notification.id);
      const response = await fetch(`${API}/firebase/mark-as-read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: getUID(),
          notificationIds: unreadIds
        })
      });

      if (response.ok) {
        setNotifications(prev => ({
          unread: [],
          read: [...prev.read, ...prev.unread.map(n => ({ ...n, is_read: true }))]
        }));
        await fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const NotificationItem = ({ notification }) => (
    <NotificationItemWrapper 
      onClick={() => markAsRead(notification.id)}
      unread={!notification.is_read}
    >
      <NotificationHeader>
        <TitleContainer>
          <NotificationTitle>{notification.title}</NotificationTitle>
          {!notification.is_read && <UnreadDot />}
        </TitleContainer>
        <Timestamp>{formatTimestamp(notification.created_at)}</Timestamp>
      </NotificationHeader>
      <NotificationContent>{notification.body}</NotificationContent>
    </NotificationItemWrapper>
  );

  return (
    <Container>
      <Header>
        <Title>Notifications</Title>
        <MarkAllButton 
          onClick={markAllAsRead} 
          disabled={notifications.unread.length === 0}
        >
          Mark all as read
        </MarkAllButton>
      </Header>

      <TabContainer>
        <TabButton 
          active={activeTab === "unread"} 
          onClick={() => setActiveTab("unread")}
        >
          Unread ({notifications.unread.length})
        </TabButton>
        <TabButton 
          active={activeTab === "read"} 
          onClick={() => setActiveTab("read")}
        >
          Read ({notifications.read.length})
        </TabButton>
      </TabContainer>

      <NotificationList>
        {isLoading ? (
          <LoadingSpinner size={24} />
        ) : (
          activeTab === "unread" ? (
            notifications.unread.length > 0 ? (
              notifications.unread.map(notification => (
                <NotificationItem key={notification.id} notification={notification} />
              ))
            ) : (
              <EmptyState>No unread notifications</EmptyState>
            )
          ) : (
            notifications.read.length > 0 ? (
              notifications.read.map(notification => (
                <NotificationItem key={notification.id} notification={notification} />
              ))
            ) : (
              <EmptyState>No read notifications</EmptyState>
            )
          )
        )}
      </NotificationList>
    </Container>
  );
};

export default MobileNotification;